import styled from 'styled-components'
import {
  AutoTable,
  BaseContainer,
  BodyCopy,
  Tooltip,
  TooltipContentWrapper,
} from 'secondstep-components'
import { themeGet } from 'styled-system'

export const FlexContainer = styled(BaseContainer)`
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: inherit;
  min-width: 0;
`

export const TextHiddenOverflow = styled.div`
  margin: 0;
  padding: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const TextContainer = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

export const TableHeaderTooltip = styled(Tooltip)`
  white-space: break-spaces;

  margin-left: ${themeGet('spacing.pink8_0_5rem')};
  i {
    color: ${themeGet('colors.blueGray')};
    font-size: 0.875rem;
  }
  /* override default tooltip breakpoint styling */
  ${themeGet('breakpoints.tablet')} {
    margin-left: 0.25rem;
    width: ${themeGet('spacing.purple24_1_5rem')};
  }

  ${TooltipContentWrapper} {
    left: 1rem;
    top: 1.75rem;
    padding: 1rem;
    border-radius: 6px;
    color: ${themeGet('colors.darkGray')};

    ::after {
      content: ' ';
      width: 0.75rem;
      height: 0.75rem;
      position: absolute;
      bottom: 93%;
      background-color: ${themeGet('colors.white')};
      transform: translate(-50%) rotate(45deg);
    }

    ${BodyCopy} {
      text-align: left;
      p {
        line-height: 1rem;
        font-size: 0.9125rem;
      }
    }
  }
`

/* ── copied from components/K8ClassesTable/component.styles ── */

export const SmallText = styled(TextHiddenOverflow)`
  font-size: 0.9125rem;
`

export const BigText = styled(TextHiddenOverflow)`
  margin-top: ${themeGet('spacing.green4_0_25rem')};
  font-family: ${themeGet('fontFamilySemibold')};
`

export const StyledTextContainer = styled(TextContainer).attrs(
  ({ numOfClasses }) => ({
    numOfClasses,
  }),
)`
  padding-left: ${props => (props.numOfClasses === 1 ? '0.125rem' : '1rem')};
`

export const ClassesTooltip = styled(TableHeaderTooltip)`
  [class*='TooltipContentWrapper'] {
    left: 4rem;
    top: 1.5rem;

    ::after {
      bottom: 90%;
      left: 29%;

      ${themeGet('breakpoints.tablet')} {
        left: 31%;
      }
    }
  }
`

export const LessonProgressTooltip = styled(TableHeaderTooltip)`
  [class*='TooltipContentWrapper'] {
    left: 4rem;
    top: 1.5rem;

    ::after {
      bottom: 90%;
      left: 29.25%;
    }

    ${themeGet('breakpoints.tablet')} {
      left: -6.25rem;

      ::after {
        left: 89.25%;
      }
    }

    ${themeGet('breakpoints.mobileLarge')} {
      left: -5.9125rem;
    }
  }
`

export const LastLessonTooltip = styled(TableHeaderTooltip)`
  [class*='TooltipContentWrapper'] {
    left: 50%;
    top: 1.5rem;

    ::after {
      left: 50%;
    }
  }
`

export const MarkedDoneTooltip = styled(TableHeaderTooltip)`
  [class*='TooltipContentWrapper'] {
    left: -5.5rem;
    top: 1.5rem;

    ::after {
      left: 85%;
    }
  }
`

/* ── ReportTableV2 ── */

export const IconContainer = styled.div.attrs(({ visible }) => ({
  visibility: visible ? 'visible' : 'hidden',
}))`
  display: ${props => (props.visible ? 'flex' : 'none')};
  visibility: ${props => props.visibility};
  margin-top: ${themeGet('spacing.green4_0_25rem')};
  margin-right: 0.625rem;

  ${themeGet('breakpoints.mobileLarge')} {
    margin-right: 0.625rem;
  }
`

export const OuterCard = styled.div`
  position: relative;
  max-width: ${themeGet('breakpoints.sizes.large')};
  margin-left: auto;
  margin-right: auto;
  min-width: 0;
  width: 100%;
  background-color: ${themeGet('colors.white')};
  border-radius: 0.625rem;
  padding: 2rem 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
  box-sizing: border-box;
`

export const TableLastUpdated = styled.span`
  position: absolute;
  top: 13px;
  right: 20px;
  z-index: 1;
  font-family: ${themeGet('fontFamily')};
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: ${themeGet('colors.gray1100')};
  pointer-events: none;
`

export const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
`

export const TitleText = styled.h2`
  font-family: ${themeGet('fontFamilyHeadingMedium')};
  font-size: 1.4375rem;
  line-height: 1.2;
  color: ${themeGet('colors.darkBlue')};
  margin: 0;
  font-weight: 600;
`

export const PillButton = styled.button`
  appearance: none;
  border: 2px solid ${themeGet('colors.gray500')};
  background-color: ${themeGet('colors.white')};
  border-radius: 0.9375rem;
  padding: 0.25rem 0.8125rem;
  font-family: ${themeGet('fontFamilySemibold')};
  font-size: 1rem;
  line-height: 1.3125rem;
  color: ${themeGet('colors.darkGray')};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
  max-width: 100%;

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.blueGray')};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }
`

export const ChevronIcon = styled.i`
  font-size: 0.625rem;
  color: ${themeGet('colors.darkBlue')};
`

/** Title-row unit range control — aliceBlue bg/border (#E5F3FD), separate from filter-row pills */
export const UnitRangePillButton = styled.button`
  appearance: none;
  border: 2px solid ${themeGet('colors.aliceBlue')};
  background-color: ${themeGet('colors.aliceBlue')};
  border-radius: 0.9375rem;
  padding: 0.25rem 0.8125rem;
  font-family: ${themeGet('fontFamilySemibold')};
  font-size: 1rem;
  line-height: 1.3125rem;
  color: ${themeGet('colors.darkGray')};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
  max-width: 100%;

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.blueGray')};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: ${props => (props.$isStatic ? 1 : 0.65)};
    pointer-events: ${props => (props.$isStatic ? 'none' : 'auto')};
  }
`

export const UnitRangeChevronIcon = styled.i`
  font-size: 0.625rem;
  color: ${themeGet('colors.darkGray')};
`

export const UnitRangeMenuOption = styled.button`
  appearance: none;
  align-self: stretch;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.375rem 1rem;
  margin: 0;
  font-family: ${themeGet('fontFamily')};
  font-size: 0.9125rem;
  color: ${themeGet('colors.darkGray')};
  cursor: pointer;
  line-height: 1.25rem;
  white-space: nowrap;

  &:hover {
    background-color: ${themeGet('colors.paleGray')};
  }

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.blueGray')};
    outline-offset: -2px;
  }
`

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
`

export const FilterLabel = styled.span`
  font-family: ${themeGet('fontFamilySemibold')};
  font-size: 1rem;
  color: ${themeGet('colors.darkGray')};
`

export const FilterButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
`

export const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  font-family: ${themeGet('fontFamily')};
  font-size: 1rem;
  line-height: 1.375rem;
  color: ${themeGet('colors.darkGray')};
`

export const SummaryDivider = styled.span`
  width: 1px;
  height: 0.875rem;
  background-color: ${themeGet('colors.gray500')};
  flex-shrink: 0;
`

export const SummaryStrong = styled.span`
  font-family: ${themeGet('fontFamilySemibold')};
`

export const SummaryWithIcon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`

export const HairlineDivider = styled.div`
  height: 2px;
  margin-bottom: -1rem;
  margin-top: -0.125rem;
  width: 100%;
  background-color: ${themeGet('colors.gray500')};
`

export const StatusDot = styled.span`
  width: 0.8125rem;
  height: 0.8125rem;
  border-radius: 50%;
  background-color: ${themeGet('colors.summerOrange')};
  flex-shrink: 0;
`

/** 10px between lesson number line and lesson title (spec). */
export const LastLessonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  width: 100%;
`

export const LastLessonNumLine = styled(SmallText)`
  margin: 0;
`

export const LastLessonTitleLine = styled(BigText)`
  margin-top: 0;
`

export const ClassNameContainer = styled.div`
  display: flex;
  align-items: center;
`

/** Lets the grid shrink below wide table intrinsic min-width so columns don’t clip off-screen. */
export const TableScrollRegion = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
`

/** Styles the learn report table directly on AutoTable from secondstep-components (container + sort/filter). */
export const ReportTableV2DataTable = styled(AutoTable)`
  table-layout: fixed;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  color: ${themeGet('colors.darkGray')};
  border: none;
  background-color: transparent;
  margin-top: 0;
  border-collapse: separate;
  border-spacing: 0;

  /* Override AutoTable default row divider: white gutter only between tbody rows (not under thead) */
  tbody tr:not(:last-child) > td {
    border-bottom: 1.25rem solid ${themeGet('colors.white')} !important;
  }

  tbody tr:last-child > td {
    border-bottom: none !important;
  }

  /* AutoTable header <tr> uses nowrap — forces table wider than viewport; allow wrapping */
  [class*='AutoTableHeaderTr'] {
    background-color: ${themeGet('colors.white')};
    border-bottom: none;
    white-space: normal;
  }

  [class*='AutoTableHeader'],
  [class*='Cell'] {
    line-height: 1.375;
  }

  [class*='AutoTableHeaderTh'] {
    background-color: ${themeGet('colors.white')};
    text-align: center;
    vertical-align: middle;
    min-width: 0;
    overflow: hidden;
    white-space: normal;
    word-break: break-word;
  }

  tbody [class*='Row'] {
    min-height: 4.25rem;
  }

  [class*='Cell'] {
    box-sizing: border-box;
    padding: 1rem 1.125rem 0.625rem;
    background-color: ${themeGet('colors.paleGray')};
    vertical-align: middle;
    min-width: 0;
    overflow: hidden;
    word-break: break-word;
  }

  thead [class*='FlexContainer'],
  tbody [class*='FlexContainer'] {
    min-width: 0;
    max-width: 100%;
  }

  tbody [class*='Row'] [class*='FlexContainer'] {
    align-items: center;
    justify-content: start;
  }

  tbody [class*='Row'] [class*='IconContainer'] {
    margin-top: 0;
    align-self: center;
  }

  [class*='Row'] {
    [class*='Cell']:first-child {
      border-top-left-radius: 0.625rem;
      border-bottom-left-radius: 0.625rem;
    }
    [class*='Cell']:last-child {
      border-top-right-radius: 0.625rem;
      border-bottom-right-radius: 0.625rem;
    }
  }

  [class*='Row'] {
    cursor: ${props => (props.$clickableRows ? 'pointer' : 'default')};
  }

  ${props =>
    props.$clickableRows
      ? `
    [class*='Row']:hover [class*='Cell'],
    [class*='Row']:focus-within [class*='Cell'] {
      background-color: ${themeGet('colors.focusBackground')(props)};
    }
    [class*='Row']:focus-visible {
      outline: 2px solid ${themeGet('colors.blueGray')(props)};
      outline-offset: 2px;
    }
  `
      : ''}
`

/** No extra inner padding — avoids StyledTextContainer padding-left + flex column stretching row height. */
export const MarkedDoneDate = styled.div`
  margin: 0;
  padding: 0;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const GradeTooltip = styled(TableHeaderTooltip)`
  [class*='TooltipContentWrapper'] {
    left: 50%;
    top: 1.5rem;

    ::after {
      left: 50%;
    }
  }
`

/** Multi-select grade dropdown (school summary); pairs with PillButton styling on the trigger. */
export const GradeFilterWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: fit-content;
  max-width: 100%;
`

export const GradeFilterButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border: 2px solid
    ${props => (props.$isOpen ? '#05807c' : themeGet('colors.gray500')(props))};
  border-radius: 0.9375rem;
  height: 1.9375rem;
  padding: 0 0.75rem;
  background-color: ${themeGet('colors.white')};
  font-family: ${themeGet('fontFamilySemibold')};
  font-size: 1rem;
  color: ${themeGet('colors.darkGray')};
  cursor: pointer;
  width: max-content;
  max-width: 100%;
  white-space: nowrap;

  &:hover {
    color: ${themeGet('colors.darkGray')};
  }

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.blueGray')};
    outline-offset: 2px;
  }
`

export const GradeFilterChevron = styled.i`
  font-size: 0.625rem;
  color: ${themeGet('colors.darkBlue')};
`

export const GradeFilterPanel = styled.div`
  position: absolute;
  z-index: 20;
  left: 0;
  top: calc(100% + 0.625rem);
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  min-width: 100%;
  width: max-content;
  max-width: calc(100vw - 2rem);
  max-height: 22rem;
  overflow-y: auto;
  overflow-x: auto;
  background-color: ${themeGet('colors.white')};
  border-radius: 0.3125rem;
  box-shadow: 0 0 1.375rem rgba(0, 0, 0, 0.2);
  padding: 0.375rem 0;
`

export const GradeFilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  align-self: stretch;
  padding: 0.375rem 1rem;
  margin: 0;
  cursor: pointer;
  font-family: ${themeGet('fontFamily')};
  font-size: 0.9125rem;
  font-weight: 600;
  color: ${themeGet('colors.darkGray')};
  white-space: nowrap;
  box-sizing: border-box;

  &:hover {
    background-color: ${themeGet('colors.paleGray')};
  }

  input {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
    appearance: none;
    border: 2px solid #05807c;
    border-radius: 0.1875rem;
    background-color: ${themeGet('colors.white')};
    background-repeat: no-repeat;
    background-position: center;
    background-size: 0.625rem 0.5rem;
    display: inline-grid;
    place-content: center;

    &:checked {
      background-color: #05807c;
      border-color: #05807c;
      /* white checkmark */
      background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10' fill='none'%3E%3Cpath d='M1 5.5L4.5 9L11 1' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }

    &:focus-visible {
      outline: 2px solid ${themeGet('colors.blueGray')};
      outline-offset: 1px;
    }
  }
`
